import { useState, MouseEvent } from "react";
import { Box, IconButton, TextField, Tooltip, Popover, List, ListItem, ListItemButton, ListItemText } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import MarkChatReadIcon from '@mui/icons-material/MarkChatRead';
import styles from "../styles";
import { ChatMessage } from "../../../interfaces/chatbotInterface";
import { getLocalStorageItem } from "../../../utility/helper";
import { getReservedChatMessagesByCreatedBy } from "../../../api/chatbot";

const ChatInput = ({
  value,
  onChange,
  onSend,
  isWaitingForAI = true,
}: {
  value: string;
  onChange: (val: string) => void;
  onSend: () => void;
  isWaitingForAI: boolean;
}) => {
  const [savedQueries, setSavedQueries] = useState<ChatMessage[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleOpenSavedQueries = async (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    const uid = getLocalStorageItem('uid');
    const savedQueries = await getReservedChatMessagesByCreatedBy(uid);
    setSavedQueries(savedQueries);
  };

  const handleCloseSavedQueries = () => {
    setAnchorEl(null);
  };

  const handleSelectQuery = (query: string) => {
    onChange(query);   // populate input field
    handleCloseSavedQueries();
  };

  return (
    <Box sx={styles.inputWrapper}>
      <TextField
        fullWidth
        size="small"
        placeholder={isWaitingForAI ? "LoanWatch Pulse is thinking..." : "Ask about Borrower X, AR, compliance..."}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && onSend()}
        disabled={isWaitingForAI}
        sx={styles.inputField}
        InputProps={{
          startAdornment: (
            <Tooltip title="Saved Queries" placement="top">
              <span>
                <IconButton
                  color="primary"
                  onClick={handleOpenSavedQueries}
                  size="small"
                >
                  <MarkChatReadIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )
        }}
      />

      <Tooltip title={isWaitingForAI ? "Please wait..." : "Send"} placement="top">
        <span>
          <IconButton
            color="primary"
            onClick={() => !isWaitingForAI && onSend()}
            sx={styles.sendButton}
            disabled={isWaitingForAI}
          >
            { isWaitingForAI ? <EmojiObjectsIcon sx={styles.blinkingIcon} /> : <SendIcon /> }
          </IconButton>
        </span>
      </Tooltip>

      {/* Saved Queries Menu */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseSavedQueries}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{
          elevation: 4,
          sx: { boxShadow: "0px 2px 4px rgba(0,0,0,0.2)" }
        }}
      >
        <Box sx={styles.savedQueryContainer}>
          <List>
            {savedQueries.map((query, idx) => (
              <ListItem key={idx} disablePadding>
                <ListItemButton sx={styles.savedQueryItemButton} onClick={() => handleSelectQuery(query.content)}>
                  <ListItemText primary={query.content} sx={styles.savedQueryItemText} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Popover>
    </Box>
  );
};

export default ChatInput;
